import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { User, Mail, Phone, Home, CheckCircle, Send, MapPin, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useCatalog } from '../lib/useCatalog';
import axios from 'axios';
import { trackLead } from '../lib/pixel';
import { SlotPicker, formatSlot } from './SlotPicker';

// Same-origin in production to avoid cross-site cookie issues on iOS Safari
const getBackendOrigin = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'smt-services17.fr' || host === 'www.smt-services17.fr' || host.endsWith('.emergent.host')) {
      return window.location.origin;
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const BACKEND_URL = getBackendOrigin();
const API = `${BACKEND_URL}/api`;

// Custom checkbox indicator component
const CheckIndicator = ({ checked, color = 'blue' }) => (
  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
    checked
      ? color === 'orange'
        ? 'bg-[hsl(32,95%,55%)] border-[hsl(32,95%,55%)]'
        : 'bg-[hsl(207,90%,54%)] border-[hsl(207,90%,54%)]'
      : 'border-gray-300 bg-white'
  }`}>
    {checked && <Check className="w-3 h-3 text-white" />}
  </div>
);

const ReservationForm = () => {
  const { services, options } = useCatalog();
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mode, setMode] = useState('booking');
  const [slot, setSlot] = useState(null);
  const [slotsKey, setSlotsKey] = useState(0);
  const [booked, setBooked] = useState(null);
  const [honeypot, setHoneypot] = useState('');
  const bookable = selectedServices.length === 1 ? services.find(s => s.id === selectedServices[0] && s.directBooking) : null;
  const isBooking = !!bookable && mode === 'booking';
  const visibleOptions = isBooking ? options.filter(o => bookable.optionCodes.includes(o.id)) : options;
  const bookingOptions = selectedOptions.filter(id => visibleOptions.some(o => o.id === id));

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleOptionToggle = (optionId) => {
    setSelectedOptions(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedServices.length === 0) {
      toast.error('Veuillez sélectionner au moins un service');
      return;
    }
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.address || !formData.city) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (isBooking) return submitBooking();
    setIsSubmitting(true);

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        message: formData.message || null,
        services: selectedServices,
        options: selectedOptions
      };

      const response = await axios.post(`${API}/quotes`, payload);

      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success(response.data.message || 'Votre demande de devis a été envoyée avec succès !');

      // Meta Pixel — Lead event on successful submission
      trackLead({
        content_name: 'Devis SMT Services',
        content_category: selectedServices.join(', ') || 'nettoyage',
      });
    } catch (error) {
      setIsSubmitting(false);
      const errorMessage = error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.';
      toast.error(errorMessage);
    }
  };

  const submitBooking = async () => {
    if (!slot) {
      toast.error('Veuillez choisir un créneau');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/reservations`, {
        first_name: formData.firstName, last_name: formData.lastName, email: formData.email, phone: formData.phone,
        address: formData.address, city: formData.city, message: formData.message || null,
        service: bookable.id, options: bookingOptions, date: slot.date, time: slot.time, website: honeypot,
      });
      setBooked(slot);
      setIsSubmitted(true);
      trackLead({ content_name: 'Réservation SMT Services', content_category: bookable.id });
    } catch (error) {
      const status = error.response?.status;
      if (status === 409) {
        toast.error('Ce créneau vient d\'être réservé. Merci d\'en choisir un autre.');
        setSlot(null);
        setSlotsKey(k => k + 1);
      } else {
        toast.error(error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
      }
    }
    setIsSubmitting(false);
  };

  if (isSubmitted && booked) {
    return (
      <section id="reservation" className="section-padding bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl" data-testid="booking-success">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[hsl(207,90%,54%)]" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Demande de réservation reçue</h2>
            <p className="text-gray-700 mb-2">Le créneau du <strong data-testid="booking-success-slot">{formatSlot(booked)}</strong> est bloqué pour vous.</p>
            <p className="text-gray-600 mb-8" data-testid="booking-pending-note">Il reste <strong>en attente de validation</strong> par SMT Services. Vous recevrez un e-mail dès sa confirmation.</p>
            <Button data-testid="new-booking-button" onClick={() => { setIsSubmitted(false); setBooked(null); setSlot(null); setSelectedServices([]); setSelectedOptions([]); }}
              className="bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,90%,44%)] text-white font-semibold px-8">Faire une nouvelle demande</Button>
          </div>
        </div>
      </section>
    );
  }

  if (isSubmitted) {
    return (
      <section id="reservation" className="section-padding bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-3xl p-12 shadow-2xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Demande envoyée !
            </h2>
            <p className="text-gray-600 mb-8">
              Merci pour votre demande. Nous vous recontacterons dans les plus brefs délais pour fixer ensemble la date d'intervention.
            </p>
            <Button
              data-testid="new-quote-button"
              onClick={() => {
                setIsSubmitted(false);
                setSelectedServices([]);
                setSelectedOptions([]);
                setFormData({
                  firstName: '', lastName: '', email: '', phone: '',
                  address: '', city: '', message: ''
                });
              }}
              className="bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,90%,44%)] text-white font-semibold px-8"
            >
              Faire une nouvelle demande
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="reservation" className="section-padding bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[hsl(207,90%,54%)] rounded-full opacity-5 blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[hsl(32,95%,55%)] rounded-full opacity-5 blur-3xl" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-[hsl(32,95%,55%)] font-semibold text-sm uppercase tracking-wider mb-4">
            Devis gratuit
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Demandez votre{' '}
            <span className="text-gradient">devis gratuit</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Remplissez le formulaire ci-dessous. Nous vous recontactons rapidement pour fixer ensemble la date d'intervention.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Services */}
            <Card className="border-0 shadow-xl h-fit">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Sparkles className="w-5 h-5 text-[hsl(207,90%,54%)]" />
                  Sélectionnez vos services
                </CardTitle>
                <CardDescription>Choisissez un ou plusieurs services</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Vitres */}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-[hsl(207,90%,54%)] uppercase tracking-wider">Vitres & Vérandas</p>
                  {services.filter(s => s.category === 'vitres').map((service) => (
                    <div
                      key={service.id}
                      data-testid={`service-${service.id}`}
                      onClick={() => handleServiceToggle(service.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        selectedServices.includes(service.id)
                          ? 'border-[hsl(207,90%,54%)] bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CheckIndicator checked={selectedServices.includes(service.id)} />
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{service.name}</h4>
                          <p className="text-sm text-gray-500">{service.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Ménage */}
                <div className="space-y-2 pt-4 border-t">
                  <p className="text-sm font-semibold text-[hsl(207,90%,54%)] uppercase tracking-wider">Ménage</p>
                  {services.filter(s => s.category === 'menage').map((service) => (
                    <div
                      key={service.id}
                      data-testid={`service-${service.id}`}
                      onClick={() => handleServiceToggle(service.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        selectedServices.includes(service.id)
                          ? 'border-[hsl(207,90%,54%)] bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CheckIndicator checked={selectedServices.includes(service.id)} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">{service.name}</h4>
                            {service.hasDeduction && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">-50% impôts</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{service.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Spécialisés */}
                <div className="space-y-2 pt-4 border-t">
                  <p className="text-sm font-semibold text-[hsl(207,90%,54%)] uppercase tracking-wider">Services Spécialisés</p>
                  {services.filter(s => s.category === 'special').map((service) => (
                    <div
                      key={service.id}
                      data-testid={`service-${service.id}`}
                      onClick={() => handleServiceToggle(service.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                        selectedServices.includes(service.id)
                          ? 'border-[hsl(207,90%,54%)] bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CheckIndicator checked={selectedServices.includes(service.id)} />
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{service.name}</h4>
                          <p className="text-sm text-gray-500">{service.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Options */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-3">Options supplémentaires :</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {visibleOptions.map((option) => (
                      <div
                        key={option.id}
                        data-testid={`option-${option.id}`}
                        onClick={() => handleOptionToggle(option.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                          selectedOptions.includes(option.id)
                            ? 'border-[hsl(32,95%,55%)] bg-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <CheckIndicator checked={selectedOptions.includes(option.id)} color="orange" />
                          <span className="text-sm font-medium text-gray-700">{option.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {bookable && (
                  <div className="pt-4 border-t space-y-3 min-w-0" data-testid="booking-panel">
                    <div className="grid grid-cols-2 gap-2">
                      {[['booking', 'Réserver un créneau'], ['quote', 'Demander un devis']].map(([m, txt]) => (
                        <button type="button" key={m} onClick={() => setMode(m)} data-testid={`mode-${m}`}
                          className={`py-2 rounded-lg border text-sm font-semibold transition-colors ${mode === m ? 'border-[hsl(207,90%,54%)] bg-blue-50 text-[hsl(207,90%,44%)]' : 'border-gray-200 text-gray-600'}`}>{txt}</button>
                      ))}
                    </div>
                    {isBooking && (
                      <SlotPicker service={bookable.id} options={bookingOptions.join(',')} value={slot} onChange={setSlot} refreshKey={slotsKey} />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right Column - Contact Form */}
            <Card className="border-0 shadow-xl h-fit lg:sticky lg:top-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <User className="w-5 h-5 text-[hsl(207,90%,54%)]" />
                  Vos coordonnées
                </CardTitle>
                <CardDescription>Remplissez vos informations de contact</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Prénom *</Label>
                    <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="Jean" required data-testid="input-firstname" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Nom *</Label>
                    <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="Dupont" required data-testid="input-lastname" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    Email *
                  </Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="jean.dupont@email.com" required data-testid="input-email" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    Téléphone *
                  </Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="06 12 34 56 78" required data-testid="input-phone" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-gray-400" />
                    Adresse *
                  </Label>
                  <Input id="address" name="address" value={formData.address} onChange={handleInputChange} placeholder="123 rue de la Paix" required data-testid="input-address" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city" className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    Ville *
                  </Label>
                  <Input id="city" name="city" value={formData.city} onChange={handleInputChange} placeholder="Chaillevette" required data-testid="input-city" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message (optionnel)</Label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleInputChange} placeholder="Décrivez votre besoin, nombre de fenêtres, surface, période souhaitée…" rows={4} data-testid="input-message" />
                </div>

                {/* Summary */}
                {selectedServices.length > 0 && (
                  <div className="bg-blue-50 rounded-xl p-4 space-y-2" data-testid="summary-box">
                    <h4 className="font-semibold text-gray-900">Récapitulatif</h4>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Services :</span>{' '}
                      {selectedServices.map(id => services.find(s => s.id === id)?.name).join(', ')}
                    </p>
                    {isBooking && slot && (
                      <p className="text-sm text-gray-600" data-testid="summary-slot"><span className="font-medium">Créneau :</span> {formatSlot(slot)} (en attente de validation)</p>
                    )}
                    {selectedOptions.length > 0 && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Options :</span>{' '}
                        {selectedOptions.map(id => options.find(o => o.id === id)?.name).join(', ')}
                      </p>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="submit-quote-button"
                  className="w-full bg-[hsl(32,95%,55%)] hover:bg-[hsl(32,95%,45%)] text-white font-semibold py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Envoi en cours...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      {isBooking ? 'Envoyer ma demande de réservation' : 'Envoyer ma demande de devis'}
                    </span>
                  )}
                </Button>

                {isBooking ? (
                  <>
                    <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                    <p className="text-xs text-gray-500 text-center" data-testid="booking-note">Le créneau est bloqué dès l'envoi, puis confirmé par SMT Services.</p>
                  </>
                ) : (
                  <p className="text-xs text-gray-500 text-center">
                    Nous vous recontactons sous 24h pour fixer ensemble la date d'intervention.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </form>
      </div>
    </section>
  );
};

// Sparkles icon
const Sparkles = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
    <path d="M5 3v4"/>
    <path d="M19 17v4"/>
    <path d="M3 5h4"/>
    <path d="M17 19h4"/>
  </svg>
);

export default ReservationForm;
